/**
 * Stable test ids for the social-media publishing wizard and posts grid. Single source of truth shared by the
 * React source (renders `data-testid`) and the Playwright suite (`getByTestId`).
 *
 * Added because the existing social e2e (`tests/e2e/social-publishing/auto-captions.test.ts`) had no ids to
 * work with and had to target hashed CSS-module classes and accessible names instead — e.g.
 * `[class*="listItem"][class*="_cursor-pointer"]:not([class*="disabled"]):not([class*="error"])` for a
 * selectable account, and `/^Next/` because the Next button's accessible name carries a trailing icon-font
 * glyph (`"Next"`) so exact matching never hits. Both break on any restyle.
 *
 * Deliberately small. Notes on the ones that are here:
 *
 * NOTE: no Playwright spec currently drives these. The social suite seeds and asserts through the
 * `__test__` harness endpoints rather than the wizard UI, and the page object built alongside these ids was
 * deleted once that restructure landed (recoverable from git history). They are kept because they are the
 * groundwork for the UI-driven cases still on the plan (GEN-*, TRM-*) and for the wider getByTestId
 * migration — but treat them as unverified until a spec actually selects on them.
 *
 * - The wizard has TWO Next controls (desktop nav and mobile footer) and both are in the DOM at their
 *   respective breakpoints, so they get distinct ids. The Playwright config runs 1440x900, which is the
 *   desktop nav — but a mobile-viewport test must not silently match the wrong one.
 * - ACCOUNT_ROW goes on every account row, selectable or not. Filtering selectable rows by CSS class was a
 *   workaround for having no ids at all; a test that seeded its own accounts should target one by name (the
 *   row renders the account label) or by index.
 * - The Schedule control has the same two-variant problem as Next: a button at the widest breakpoint and a
 *   popup-menu item below it. Only the BUTTON gets an id — the menu item renders through a legacy `.jsx`
 *   PopupMenuItem that does not forward `data-testid`, and modifying it for a breakpoint the suite may not hit
 *   is not worth it; a spec needing that breakpoint should fall back to the menu item by its text. Note the
 *   scheduling default is already the NEXT HOUR
 *   (`getSchedulingDateAtNextHour`), so a test that only needs "scheduled in the future" can confirm without
 *   touching the date picker — and confirmation reuses WIZARD_NEXT_BUTTON, which doubles as Done while the
 *   schedule details are showing.
 * - PREPARING_DESIGN_GATE marks the publish-step soft gate. It is the only signal that the graphics-readiness
 *   poll is in flight, and it renders conditionally, so a test asserting the gate opened needs it to attach to
 *   rather than a sleep.
 */
export enum SocialMediaTestId {
  WIZARD_NEXT_BUTTON = 'SOCIAL_WIZARD_NEXT_BUTTON',
  WIZARD_MOBILE_NEXT_BUTTON = 'SOCIAL_WIZARD_MOBILE_NEXT_BUTTON',
  WIZARD_ACCOUNT_ROW = 'SOCIAL_WIZARD_ACCOUNT_ROW',
  WIZARD_PREPARING_DESIGN_GATE = 'SOCIAL_WIZARD_PREPARING_DESIGN_GATE',
  WIZARD_SCHEDULE_BUTTON = 'SOCIAL_WIZARD_SCHEDULE_BUTTON',
  WIZARD_SHARE_IN_TEAM_TOGGLE = 'SOCIAL_WIZARD_SHARE_IN_TEAM_TOGGLE',
  WIZARD_CAPTION_WRITING_SKELETONS = 'SOCIAL_WIZARD_CAPTION_WRITING_SKELETONS',
}
